import "./homePage.css";
import GreenArrowIcon from "@/assets/images/GreenArrowIcon.svg";
import TotalUploadIcon from "@/assets/images/credit-card-blue.svg";
import AmountUsedIcon from "@/assets/images/shopping-cart-yellow.svg";
import AmountLeftIcon from "@/assets/images/credit-card-green.svg";
import UnpaidSubsidiesIcon from "@/assets/images/money-dollar.svg";
import CreditBalanceIcon from "@/assets/images/balance.svg";
import { MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";
import { HomePagePeriod } from "@/models/enums";
import BaseTooltip from "@/components/common/baseTooltip";
import BaseInput from "../common/baseInput";
import { useEffect, useState } from "react";
import { CacheProvider } from "@emotion/react";
import authStore from "@/stores/authStore";
import rtlPlugin from "stylis-plugin-rtl";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import BaseButton from "../common/baseButton";
import { useNavigate } from "react-router-dom";
import BaseSignButton from "../common/baseSignButton";
import HomePageOrderChart from "./homePageOrderChart";
import HomePageOrderFooter from "./homePageOrderFooter";
import HomePageBudgetFooter from "./homePageBudgetFooter";
import HomePageBudgetChart from "./homePageBudgetChart";
import { DashboardBudgetResponse, DashboardDataRequest, DashboardDataResponse } from "@/models/homePage";
import popupService from "@/services/popupService";
import api from "@/services/api";
import { checkPermissionsWithAlert, checkPermissionsWithoutAlert, formatAmountWithoutDecimals } from "@/util";
import HomePageDoughnut from "./homePageDoughnut";
import homePageStore from "@/stores/homePageStore";
import { PermissionName } from "@/models/apiModels";

const cacheLtr = createCache({
    key: "muiltr",
});

const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
});

const HomePage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isBudgetChart, setIsBudgetChart] = useState(false);
    const [dashboardData, setDashboardData] = useState<DashboardDataResponse>({
        totalUploadAmount: 0,
        amountUsedByEmployees: 0,
        totalAvailableBalance: 0,
        unpaidSubsidies: 0,
        creditBalance: 0,
        totalBudget: null,
        orderSummary: null,
        doughnutData: null,
    });
    const [dashboardDataRequest, setDashboardDataRequest] = useState<DashboardDataRequest>({
        orderSummarySelect: HomePagePeriod.ByMonth,
        totalUsedSelect: HomePagePeriod.ByMonth,
    });
    const [budgetDataRequest, setBudgetDataRequest] = useState<HomePagePeriod>(HomePagePeriod.ByMonth);
    const TimePeriodEnum = [
        {
            id: HomePagePeriod.ByMonth,
            text: t("homepage.Display.ByMonth"),
        },
        {
            id: HomePagePeriod.ByWeek,
            text: t("homepage.Display.ByWeek"),
        },
        {
            id: HomePagePeriod.ByYear,
            text: t("homepage.Display.ByYear"),
        },
    ];

    const handleChangeBudgetChart = (event: any) => {
        setBudgetDataRequest(() => event.target.value);
        homePageStore.setDashboardBudgetData(new DashboardBudgetResponse());
    };

    const startFetching = async () => {
        if (!checkPermissionsWithAlert(PermissionName.OrganizationDashboard)) {
            return;
        }
        try {
            popupService.showLoading();
            const response = await api.getDashboardData(
                dashboardDataRequest.orderSummarySelect,
                dashboardDataRequest.totalUsedSelect
            );
            setDashboardData(response.data);
            popupService.close();
        } catch (err) {
            await popupService.showApiError(err);
            return;
        }
    };

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            startFetching();
        }
        return () => {
            ignore = true;
        };
    }, [dashboardDataRequest]);

    return (
        <div>
            {checkPermissionsWithoutAlert(PermissionName.OrganizationDashboard) ? (
                <CacheProvider value={authStore.isRtl ? cacheRtl : cacheLtr}>
                    <div className="hp-container">
                        <div className="hp-side-top">
                            <div className="hp-upload-amount">
                                <div>
                                    <div style={{ display: "flex", justifyContent: "center" }}>
                                        <div className="upload-amount-circle">
                                            <img src={TotalUploadIcon} alt="UploadAmount"></img>
                                        </div>
                                    </div>
                                    <div
                                        style={{ paddingTop: 15, display: "flex", justifyContent: "center" }}
                                        className="font-style-36-bold"
                                    >
                                        {formatAmountWithoutDecimals(dashboardData.totalUploadAmount ?? 0, 1)}
                                    </div>
                                    <div
                                        style={{ paddingTop: 10, display: "flex", justifyContent: "center" }}
                                        className="font-style-16"
                                    >
                                        {t("homepage.UploadAmount")}
                                    </div>
                                </div>
                            </div>
                            <div className="hp-amount-used">
                                <div>
                                    <div style={{ display: "flex", justifyContent: "center" }}>
                                        <div className="amount-used-circle">
                                            <img src={AmountUsedIcon} alt="AmountUsed"></img>
                                        </div>
                                    </div>
                                    <div
                                        style={{ paddingTop: 15, display: "flex", justifyContent: "center" }}
                                        className="font-style-36-bold"
                                    >
                                        {formatAmountWithoutDecimals(
                                            Math.abs(dashboardData.amountUsedByEmployees) ?? 0,
                                            1
                                        )}
                                    </div>
                                    <div
                                        style={{ paddingTop: 10, display: "flex", justifyContent: "center" }}
                                        className="font-style-16"
                                    >
                                        {t("homepage.AmountUsed")}
                                    </div>
                                </div>
                            </div>
                            <div className="hp-amount-left">
                                <div>
                                    <div style={{ display: "flex", justifyContent: "center" }}>
                                        <div className="amount-left-circle">
                                            <img src={AmountLeftIcon} alt="AmountLeft"></img>
                                        </div>
                                    </div>
                                    <div
                                        style={{ paddingTop: 15, display: "flex", justifyContent: "center" }}
                                        className="font-style-36-bold"
                                    >
                                        {formatAmountWithoutDecimals(dashboardData.totalAvailableBalance ?? 0, 1)}
                                    </div>
                                    <div
                                        style={{ paddingTop: 10, display: "flex", justifyContent: "center" }}
                                        className="font-style-16"
                                    >
                                        {t("homepage.TotalAvailableBalance")}
                                    </div>
                                </div>
                            </div>
                            <div className="hp-unpaid-subsidies">
                                <div>
                                    <div style={{ display: "flex", justifyContent: "center" }}>
                                        <div className="unpaid-subsidies-circle">
                                            <img src={UnpaidSubsidiesIcon} alt="AmountLeft"></img>
                                        </div>
                                    </div>
                                    <div
                                        style={{ paddingTop: 15, display: "flex", justifyContent: "center" }}
                                        className="font-style-36-bold"
                                    >
                                        {formatAmountWithoutDecimals(dashboardData.unpaidSubsidies ?? 0, 1)}
                                    </div>
                                    <div
                                        style={{ paddingTop: 10, display: "flex", justifyContent: "center" }}
                                        className="font-style-16"
                                    >
                                        {t("digitalWallet.UnpaidSubsidies")}
                                    </div>
                                </div>
                            </div>
                            <div className="hp-credit-balance">
                                <div>
                                    <div style={{ display: "flex", justifyContent: "center" }}>
                                        <div className="credit-balance-circle">
                                            <img src={CreditBalanceIcon} alt="AmountLeft"></img>
                                        </div>
                                    </div>
                                    <div
                                        style={{ paddingTop: 15, display: "flex", justifyContent: "center" }}
                                        className="font-style-36-bold"
                                    >
                                        {formatAmountWithoutDecimals(dashboardData.creditBalance ?? 0, 1)}
                                    </div>
                                    <div
                                        style={{ paddingTop: 10, display: "flex", justifyContent: "center" }}
                                        className="font-style-16"
                                    >
                                        {t("digitalWallet.LalunCreditBalance")}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="hp-side-bottom">
                            <div className="hp-total-used-budget">
                                <div>
                                    <div style={{ display: "flex", gap: 9 }}>
                                        <div className="font-style-24-bold">{t("homepage.TotalBudget")}</div>
                                        <BaseTooltip placement="bottom" title={t("homepage.TotalInfoText")}>
                                            <div
                                                style={{ paddingTop: 5 }}
                                                className="icon-f icon-CircleInfoIcon fs-22"
                                            ></div>
                                        </BaseTooltip>
                                    </div>
                                    <div style={{ paddingTop: 25 }}>
                                        <BaseInput
                                            select
                                            type="search"
                                            size="small"
                                            value={dashboardDataRequest.totalUsedSelect ?? ""}
                                            onChange={(event: any) =>
                                                setDashboardDataRequest((prevData) => ({
                                                    ...prevData,
                                                    totalUsedSelect: event.target.value,
                                                }))
                                            }
                                            sx={{
                                                width: "147px",
                                            }}
                                        >
                                            {TimePeriodEnum.map((option) => (
                                                <MenuItem key={option.id} value={option.id}>
                                                    {option.text}
                                                </MenuItem>
                                            ))}
                                        </BaseInput>
                                    </div>
                                </div>
                                <div className="used-budget-content">
                                    <div className="used-budget-circle">
                                        <span className="font-style-bold-30-white">
                                            {formatAmountWithoutDecimals(
                                                dashboardData.totalBudget?.totalUsedBudget ?? 0,
                                                dashboardData.totalBudget?.currencyId
                                            )}
                                        </span>
                                    </div>
                                    <div style={{ display: "flex", gap: 11, paddingTop: 15, height: 45 }}>
                                        <div style={{ paddingTop: 4 }}>
                                            <img
                                                style={{
                                                    transform:
                                                        Number(dashboardData.totalBudget?.totalBudgetPercent) <= 0
                                                            ? "rotate(180deg)"
                                                            : "none",
                                                }}
                                                src={GreenArrowIcon}
                                                alt="GreenArrow"
                                            />
                                        </div>
                                        <div style={{ display: "grid" }}>
                                            <span className="font-style-24-half-bold">
                                                {dashboardData.totalBudget?.totalBudgetPercent?.toFixed(0)}%
                                            </span>
                                            <span className="font-style-16">
                                                {dashboardData.totalBudget?.percentTextShow
                                                    ? t("homepage.MoreThenLast")
                                                    : t("homepage.LessThenLast")}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="hp-order-summary">
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                                    <div className="hp-order-summary-buttons">
                                        <div>
                                            <BaseSignButton
                                                style={{ padding: "0px 16px" }}
                                                className={isBudgetChart ? "hp-chart-button" : "hp-chart-button-active"}
                                                onClick={() => setIsBudgetChart(false)}
                                            >
                                                {t("homepage.OrderSummary")}
                                            </BaseSignButton>
                                        </div>
                                        <div>
                                            <BaseSignButton
                                                style={{ padding: "0px 16px" }}
                                                className={isBudgetChart ? "hp-chart-button-active" : "hp-chart-button"}
                                                onClick={() => setIsBudgetChart(true)}
                                            >
                                                {t("homepage.Budget")}
                                            </BaseSignButton>
                                        </div>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                        <div>
                                            {isBudgetChart ? (
                                                <BaseInput
                                                    select
                                                    type="search"
                                                    size="small"
                                                    value={budgetDataRequest ?? ""}
                                                    sx={{
                                                        width: "147px",
                                                    }}
                                                    onChange={(event: any) => handleChangeBudgetChart(event)}
                                                >
                                                    {TimePeriodEnum.map((option) => (
                                                        <MenuItem key={option.id} value={option.id}>
                                                            {option.text}
                                                        </MenuItem>
                                                    ))}
                                                </BaseInput>
                                            ) : (
                                                <BaseInput
                                                    select
                                                    type="search"
                                                    size="small"
                                                    value={dashboardDataRequest.orderSummarySelect ?? ""}
                                                    sx={{
                                                        width: "147px",
                                                    }}
                                                    onChange={(event: any) =>
                                                        setDashboardDataRequest((prevData) => ({
                                                            ...prevData,
                                                            orderSummarySelect: event.target.value,
                                                        }))
                                                    }
                                                >
                                                    {TimePeriodEnum.map((option) => (
                                                        <MenuItem key={option.id} value={option.id}>
                                                            {option.text}
                                                        </MenuItem>
                                                    ))}
                                                </BaseInput>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
                                    <div className="hp-charts-style">
                                        {isBudgetChart ? (
                                            <HomePageBudgetChart budgetPeriod={budgetDataRequest} />
                                        ) : (
                                            <HomePageOrderChart orderSummary={dashboardData.orderSummary} />
                                        )}
                                    </div>
                                </div>
                                <div style={{ display: "grid" }}>
                                    {isBudgetChart ? <HomePageBudgetFooter /> : <HomePageOrderFooter />}
                                </div>
                            </div>
                            <div className="hp-side-right">
                                <div>
                                    <div className="font-style-24-bold">
                                        {t("homepage.BudgetSince")}{" "}
                                        {dashboardData.doughnutData?.firstMonthOfCurrentYear}
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10, paddingTop: 20 }}>
                                        <div className="icon-f icon-CircleInfoIcon fs-22"></div>
                                        <div className="font-style">{t("homepage.BudgetInfoText")}</div>
                                    </div>
                                </div>
                                <div className="hp-doughnut-block">
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                            position: "relative",
                                            alignItems: "center",
                                        }}
                                    >
                                        <div className="hp-doughnut-percent-element">
                                            {dashboardData.doughnutData?.usedPercent?.toFixed(0) ?? 0}%
                                        </div>
                                        <HomePageDoughnut doughnutData={dashboardData.doughnutData} />
                                    </div>
                                    <div className="hp-doughnut-budget">
                                        <div>
                                            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                                <div className="used-point"></div>
                                                {t("homepage.Used")}
                                            </div>
                                            <div
                                                style={{ paddingLeft: 14, paddingTop: 5 }}
                                                className="font-style-24-bold"
                                            >
                                                {formatAmountWithoutDecimals(dashboardData.doughnutData?.used ?? 0, 1)}
                                            </div>
                                        </div>
                                        <div className="hp-vertical-divider"></div>
                                        <div>
                                            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                                <div className="left-point"></div>
                                                {t("homepage.Left")}
                                            </div>
                                            <div
                                                style={{ paddingLeft: 14, paddingTop: 5 }}
                                                className="font-style-24-bold"
                                            >
                                                {formatAmountWithoutDecimals(dashboardData.doughnutData?.left ?? 0, 1)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-end" }}>
                                    <BaseButton
                                        onClick={() => navigate("/digital-wallet/list")}
                                        style={{ padding: "0px 29.5px" }}
                                        className="basic-button"
                                    >
                                        {t("homepage.SeeWallets")}
                                    </BaseButton>
                                </div>
                            </div>
                        </div>
                    </div>
                </CacheProvider>
            ) : (
                ""
            )}
        </div>
    );
};

export default HomePage;
